import { AnyObject } from 'chart.js/types/basic';
import { pick } from 'es-toolkit/compat';
import { AnnotationWorkingStateValues } from 'interfaces/annotation';
import { StainingMethod } from 'interfaces/stainType';
import * as yup from 'yup';
import { RequiredArraySchema } from 'yup/lib/array';

export enum TrainingStep {
  ModelSelection,
  AnnotationAssignments,
  InputSlideList,
  TargetClassesForTraining,
  PreviewTrainingPlan,
}

export const stepsOrder = [
  TrainingStep.ModelSelection,
  TrainingStep.AnnotationAssignments,
  TrainingStep.InputSlideList,
  TrainingStep.TargetClassesForTraining,
  TrainingStep.PreviewTrainingPlan,
];

type RequiredStringArraySchema = RequiredArraySchema<yup.StringSchema<string, AnyObject, string>, AnyObject, string[]>;

const formSchema = {
  stainingMethod: yup.string().required('Staining Method is a required field'),
  stainId: yup.string().optional(), // To tag the training for a specific stain, we support direct stain selection
  baseModel: yup.string().required(),
  modelType: yup.string().required(),
  annotationAssignmentIds: yup
    .array(yup.number())
    .required('At least one annotation assignment is required')
    .min(1, 'At least one annotation assignment is required'),
  slideIds: yup.array(yup.string()).required('At least one slide is required').min(1, 'At least one slide is required'),
  trainingClassToAnnotationOptions: yup
    .object<Record<string, RequiredStringArraySchema>>()
    .required('Mapping training classes to annotation options is required'),
  disabledTrainingTargets: yup.array(yup.string()).optional(),
  customTrainingPlanText: yup.string().optional(),
  workingStatesToUse: yup.array(yup.string().oneOf(AnnotationWorkingStateValues, 'Invalid working state')).optional(),
};

export const validationSchema = {
  [TrainingStep.ModelSelection]: yup.object(pick(formSchema, ['stainId', 'stainingMethod', 'model', 'modelType'])),
  [TrainingStep.AnnotationAssignments]: yup.object(pick(formSchema, ['annotationAssignmentIds'])),
  [TrainingStep.InputSlideList]: yup.object(pick(formSchema, ['slideIds'])),
  [TrainingStep.TargetClassesForTraining]: yup.object(pick(formSchema, ['targetClassAnnotationOptions'])),
  [TrainingStep.PreviewTrainingPlan]: yup.object(pick(formSchema, ['customTrainingPlanText'])),
};

export const fullValidationSchema = yup.object(formSchema);

export type IFormValues = yup.InferType<typeof fullValidationSchema> & {
  stainingMethod: StainingMethod;
};

export const defaultValues: Partial<IFormValues> = {};
